import axios from 'axios';
import parse from 'html-react-parser';
import { Fragment, useEffect, useRef, useState } from "react";
import { Spinner, Tab } from "react-bootstrap";
import Select from 'react-select';
import { BACKEND_SERVER_URL } from '../../../http/http-config';


import DeleteIcon from '@mui/icons-material/Delete';
import ButtonMUI from '@mui/material/Button';
import { FaEdit } from "react-icons/fa";
import { SlArrowLeft, SlArrowRight } from "react-icons/sl";
import { toast } from 'react-toastify';
import '../QuestionBank/QuestionBank.css';

import { Stack } from "@mui/material";
import { deleteItem, getUGCQuestionCount, getUGCQuestions, sortInAlphabeticalOrder } from "../common-helpers/commonServices";
import UGC_QuestionBankModal from "./UGC_QuestionBankModal";

const UGC_QuestionBank = () => {
  const childRef = useRef();
  const [selectedQuestionNumber, setSelectedQuestionNumber] = useState(1);
  const [totalQuestionInQuiz, setTotalQuestionInQuiz] = useState(0);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState({});
  const [showQuestion, setShowQuestion] = useState(false);
  const [loadQuestions, setLoadQuestions] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionOffset, setQuestionOffset] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchQuestion = () => {
    const qNum = parseInt(searchQuery, 10);
    if (isNaN(qNum) || qNum <= 0) {
      toast.error("Please enter a valid question number");
      return;
    }
    if (qNum > totalQuestionInQuiz) {
      toast.error(`Question number must be between 1 and ${totalQuestionInQuiz}`);
      return;
    }

    setSelectedQuestionNumber(qNum);

    const existingQuestion = quizQuestions.find((q) => q.questionNumber === qNum);
    if (!existingQuestion) {
      const targetOffset = Math.floor((qNum - 1) / 100) * 100 + 1;
      setQuestionOffset(targetOffset);
      setLoadQuestions(prev => !prev);
    }
  };

  // Filter states
  const [subjects, setSubjects] = useState([]);
  const [sections, setSections] = useState([]);
  const [topics, setTopics] = useState([]);

  const [selectedExamType, setSelectedExamType] = useState("All Exam Types");
  const [selectedSubject, setSelectedSubject] = useState({ Sn: 0, Subject: "All SUBJECTS" });
  const [selectedSection, setSelectedSection] = useState({ Sn: 0, Section: "All TOPICS" });
  const [selectedTopic, setSelectedTopic] = useState({ Sn: 0, Topic: "All SUB TOPICS" });

  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [filteredSections, setFilteredSections] = useState([]);
  const [filteredTopics, setFilteredTopics] = useState([]);

  // Fetch lookup data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subjectsRes, sectionsRes, topicsRes] = await Promise.all([
          axios.get(BACKEND_SERVER_URL + '/ugc/subjects'),
          axios.get(BACKEND_SERVER_URL + '/ugc/sections'),
          axios.get(BACKEND_SERVER_URL + '/ugc/topics')
        ]);
        setSubjects(sortInAlphabeticalOrder(subjectsRes.data.subjects, 'Subject'));
        setSections(sortInAlphabeticalOrder(sectionsRes.data.sections, 'Section'));
        setTopics(sortInAlphabeticalOrder(topicsRes.data.topics, 'Topic'));
      } catch (error) {
        console.error("Error fetching filter data:", error);
      }
    };
    fetchData();
  }, []);


  useEffect(() => {
    setLoadingQuestions(true);
    const filters = {};
    if (selectedExamType !== 'All Exam Types') filters.examType = selectedExamType;
    if (selectedSubject.Sn !== 0) filters.subjectId = selectedSubject.Sn;
    if (selectedSection.Sn !== 0) filters.sectionId = selectedSection.Sn;
    if (selectedTopic.Sn !== 0) filters.topicId = selectedTopic.Sn;
    console.log("Fetching questions with filters:", filters, "offset:", questionOffset);

    getUGCQuestions(questionOffset, filters).then(res => {
      if (questionOffset === 1) {
        setQuizQuestions(res);
        setSelectedQuestionNumber(prev => {
          if (res.some(q => q.questionNumber === prev)) {
            return prev;
          }
          return res.length > 0 ? res[0].questionNumber : 1;
        });
      } else {
        setQuizQuestions((preValue) => {
          const newBatchNumbers = res.map(q => q.questionNumber);
          const filteredPreValue = preValue.filter(q => !newBatchNumbers.includes(q.questionNumber));
          const combined = [...filteredPreValue, ...res];
          return combined.sort((a, b) => a.questionNumber - b.questionNumber);
        });
      }
      setLoadingQuestions(false);
    }
    ).catch(error => {
      setLoadingQuestions(false);
      console.log(error)
      toast.error(error.response?.data?.message || "Error loading questions")
    })

    getUGCQuestionCount(filters).then(res => {
      setTotalQuestionInQuiz(res);
    }).catch(error => {
      toast.error(error.response?.data?.message || "Error loading question count")
    })

  }, [loadQuestions, questionOffset, selectedExamType, selectedSubject, selectedSection, selectedTopic])

  useEffect(() => {
    let selectedQ = quizQuestions.find(question => question.questionNumber == selectedQuestionNumber)
    if (selectedQ?.questionNumber) {
      setSelectedQuestion(selectedQ)
      setCorrectAnswer(selectedQ?.correctAnswer?.toLowerCase())
      setShowQuestion(true)
    } else {
      setSelectedQuestion({})
      setShowQuestion(false)
    }

  }, [selectedQuestionNumber, quizQuestions])

  const loadMoreQuestions = () => {
    setQuestionOffset((previous) => (previous + 100));
    setLoadQuestions(!loadQuestions);
  }

  const selectExamTypeHandler = (examType) => {
    setSelectedExamType(examType);
    setSelectedSubject({ Sn: 0, Subject: "All SUBJECTS" });
    setSelectedSection({ Sn: 0, Section: "All TOPICS" });
    setSelectedTopic({ Sn: 0, Topic: "All SUB TOPICS" });

    if (examType === 'All Exam Types') {
      setFilteredSubjects([]);
      setFilteredSections([]);
      setFilteredTopics([]);
    } else {
      setFilteredSubjects(sortInAlphabeticalOrder(subjects.filter(s => s.ExamType === examType), 'Subject'));
    }
    // Reset questions and fetch with new filters
    setQuestionOffset(1);
    setSelectedQuestionNumber(1);
    setLoadQuestions(!loadQuestions);
  }

  const selectSubjectHandler = (subject) => {
    setSelectedSubject(subject);
    setSelectedSection({ Sn: 0, Section: "All TOPICS" });
    setSelectedTopic({ Sn: 0, Topic: "All SUB TOPICS" });

    if (subject.Sn === 0) {
      setFilteredSections([]);
      setFilteredTopics([]);
    } else {
      setFilteredSections(sortInAlphabeticalOrder(sections.filter(s => s.SubjectID === subject.Sn), 'Section'));
    }
    setQuestionOffset(1);
    setSelectedQuestionNumber(1);
    setLoadQuestions(!loadQuestions);
  }

  const selectSectionHandler = (section) => {
    setSelectedSection(section);
    setSelectedTopic({ Sn: 0, Topic: "All SUB TOPICS" });

    if (section.Sn === 0) {
      setFilteredTopics([]);
    } else {
      setFilteredTopics(sortInAlphabeticalOrder(topics.filter(t => t.SectionID === section.Sn), 'Topic'));
    }
    setQuestionOffset(1);
    setSelectedQuestionNumber(1);
    setLoadQuestions(!loadQuestions);
  }

  const selectTopicHandler = (topic) => {
    setSelectedTopic(topic);
    setQuestionOffset(1);
    setSelectedQuestionNumber(1);
    setLoadQuestions(!loadQuestions);
  }

  return (
    <Fragment >
      <div className="card p-0 rounded-0 mb-0 ps-0">
        <div className="row gx-0 ">
          <div className="col-12 col-lg-3 bg-light ">
            <div className="d-flex justify-content-between align-items-center mt-1 p-1">
              <button type="button" className="btn btn-dark btn-xs"
                style={{ backgroundColor: '#274065', borderColor: '#274065' }}
                onClick={() => childRef.current.openModal({
                  examType: selectedExamType === 'All Exam Types' ? 'GENERAL APTITUDE' : selectedExamType,
                  subject: selectedSubject.Sn !== 0 ? selectedSubject.Subject : 'Select Subject Type',
                  subjectId: selectedSubject.Sn !== 0 ? selectedSubject.Sn : 0,
                  section: selectedSection.Sn !== 0 ? selectedSection.Section : 'Select TOPICS',
                  sectionId: selectedSection.Sn !== 0 ? selectedSection.Sn : 0,
                  topic: selectedTopic.Sn !== 0 ? selectedTopic.Topic : 'Select SUB TOPICS',
                  topicId: selectedTopic.Sn !== 0 ? selectedTopic.Sn : 0,
                })}
              >
                + Add
              </button>
              <div
                className="d-flex align-items-center px-3 py-1"
                style={{
                  background: "linear-gradient(135deg, #274065, #1a6d5e)",
                  borderRadius: "6px",
                  boxShadow: "0 2px 6px rgba(39, 64, 101, 0.3)",
                }}
              >
                <span style={{ color: "#ddd", fontSize: "12px", fontWeight: "600", letterSpacing: "0.5px" }}>
                  Total Questions:
                </span>
                <span
                  className="ms-2"
                  style={{
                    color: "#fff",
                    fontSize: "16px",
                    fontWeight: "800",
                  }}
                >
                  {totalQuestionInQuiz}
                </span>
              </div>
            </div>
            <div className="px-2 mt-2">
              <div className="input-group">
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder="Search Q.No..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearchQuestion(); }}
                />
                <button className="btn btn-primary btn-sm" onClick={handleSearchQuestion}>
                  Search
                </button>
              </div>
            </div>
            <div className="email-left-box dlab-scroll pt-2 px-1" >
              <div className="col-sm-12 mt-0 ms-2">
                <div className="d-flex justify-content-start flex-wrap">
                  {quizQuestions?.map((questionBoxData, index) => (
                    <div
                      key={questionBoxData.questionNumber}
                      className={` d-inline-flex justify-content-center col-2 cursor-pointer border-2 rounded-circle m-2   ${questionBoxData.questionNumber === selectedQuestionNumber ? "bg-dark text-white" : ""}`}
                      onClick={() => setSelectedQuestionNumber(questionBoxData.questionNumber)}
                    >
                      <span className="px-1 py-2 fw-bold ">  {questionBoxData.questionNumber}</span>

                    </div>
                  ))}
                </div>
              </div>
              <button
                className="btn btn-secondary btn-sm w-50 mt-3"
                onClick={loadMoreQuestions}
              >
                {loadingQuestions ? <Spinner size="sm" animation="border" variant="" /> : <span>{"Load more..."}</span>}
              </button>
            </div>
          </div>
          <div className="col-12 col-lg-9">
            <div className="email-right-box ">
              {<Tab.Container defaultActiveKey="Import">
                <div className="p-3" style={{ backgroundColor: "#f8f9fa" }}>
                  <div className="row g-2 mb-3">
                    <div className="col-md-3">
                      <label className="form-label mb-1 fw-bold" style={{ fontSize: '13px', color: '#2980b9', width: '100%', textAlign: 'center' }}>SECTIONS</label>
                      <Select
                        value={[
                          { value: 'All Exam Types', label: 'All SECTIONS' },
                          { value: 'GENERAL APTITUDE', label: 'Teaching & Research Aptitude' },
                          { value: 'SUBJECT SPECIFIC', label: 'Academic Subjects' }
                        ].find(opt => opt.value === selectedExamType)}
                        onChange={(opt) => selectExamTypeHandler(opt.value)}
                        options={[
                          { value: 'All Exam Types', label: 'All SECTIONS' },
                          { value: 'GENERAL APTITUDE', label: 'Teaching & Research Aptitude' },
                          { value: 'SUBJECT SPECIFIC', label: 'Academic Subjects' }
                        ]}
                        styles={{ control: (p) => ({ ...p, minHeight: '35px', fontSize: '12px', borderColor: '#00a99d' }) }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label mb-1 fw-bold" style={{ fontSize: '13px', color: '#16a085', width: '100%', textAlign: 'center' }}>SUBJECTS</label>
                      <Select
                        isDisabled={selectedExamType === 'All Exam Types'}
                        value={{ value: selectedSubject, label: selectedSubject.Subject }}
                        onChange={(opt) => selectSubjectHandler(opt.value)}
                        options={[{ Sn: 0, Subject: 'All SUBJECTS' }, ...filteredSubjects].map(s => ({ value: s, label: s.Subject }))}
                        styles={{ control: (p) => ({ ...p, minHeight: '35px', fontSize: '12px', borderColor: '#00a99d' }) }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label mb-1 fw-bold" style={{ fontSize: '13px', color: '#8e44ad', width: '100%', textAlign: 'center' }}>TOPICS</label>
                      <Select
                        isDisabled={selectedSubject.Sn === 0}
                        value={{ value: selectedSection, label: selectedSection.Section }}
                        onChange={(opt) => selectSectionHandler(opt.value)}
                        options={[{ Sn: 0, Section: 'All TOPICS' }, ...filteredSections].map(s => ({ value: s, label: s.Section }))}
                        styles={{ control: (p) => ({ ...p, minHeight: '35px', fontSize: '12px', borderColor: '#00a99d' }) }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label mb-1 fw-bold" style={{ fontSize: '13px', color: '#e67e22', width: '100%', textAlign: 'center' }}>SUB TOPICS</label>
                      <Select
                        isDisabled={selectedSection.Sn === 0}
                        value={{ value: selectedTopic, label: selectedTopic.Topic }}
                        onChange={(opt) => selectTopicHandler(opt.value)}
                        options={[{ Sn: 0, Topic: 'All SUB TOPICS' }, ...filteredTopics].map(t => ({ value: t, label: t.Topic }))}
                        styles={{ control: (p) => ({ ...p, minHeight: '35px', fontSize: '12px', borderColor: '#00a99d' }) }}
                      />
                    </div>
                  </div>

                  <div
                    className="d-flex flex-wrap justify-content-between align-items-center gap-3 p-3 bg-white"
                    style={{
                      borderRadius: "10px",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <div className="px-2">
                      <h7 className="mb-0 fw-bold" style={{ color: "#274065" }}>
                        Q. No: <span style={{ color: "#e74c3c", fontWeight: "800" }}>{selectedQuestionNumber}</span>
                      </h7>
                    </div>
                    <div className="px-2">
                      <h7 className="mb-0 fw-bold" style={{ color: "#274065" }}>
                        SECTIONS: <span style={{ color: "#2980b9", fontWeight: "800" }}>{selectedQuestion?.examType === "GENERAL APTITUDE" ? "Teaching & Research Aptitude" : selectedQuestion?.examType === "SUBJECT SPECIFIC" ? "Academic Subjects" : selectedQuestion?.examType}</span>
                      </h7>
                    </div>
                    <div className="px-2">
                      <h7 className="mb-0 fw-bold" style={{ color: "#274065" }}>
                        SUBJECT: <span style={{ color: "#16a085", fontWeight: "800" }}>{selectedQuestion?.subject}</span>
                      </h7>
                    </div>
                    <div className="px-2">
                      <h7 className="mb-0 fw-bold" style={{ color: "#274065" }}>
                        TOPICS: <span style={{ color: "#8e44ad", fontWeight: "800" }}>{selectedQuestion?.section}</span>
                      </h7>
                    </div>
                    <div className="px-2">
                      <h7 className="mb-0 fw-bold" style={{ color: "#274065" }}>
                        SUB TOPICS: <span style={{ color: "#e67e22", fontWeight: "800" }}>{selectedQuestion?.topic || "N/A"}</span>
                      </h7>
                    </div>
                  </div>
                </div>

                <div className="col-xl-12 col-xxl-12 col-lg-12 ">
                  <div className="card h-auto">
                    <div className="card-footer p-2">
                      <div className="row d-flex justify-content-between">
                        <div className="d-flex justify-content-between">
                          <ButtonMUI variant="outlined" color="secondary" startIcon={<SlArrowLeft size={10} />} onClick={() => setSelectedQuestionNumber(selectedQuestionNumber - 1)}
                            disabled={selectedQuestionNumber == 1}>
                            Previous
                          </ButtonMUI>
                          <ButtonMUI variant="outlined" color="success" endIcon={<SlArrowRight size={10} />} onClick={() => setSelectedQuestionNumber(selectedQuestionNumber + 1)} disabled={selectedQuestionNumber == quizQuestions?.length}>
                            Next
                          </ButtonMUI>
                        </div>
                      </div>
                    </div>
                    <div className="card-body pt-0 ">
                      <div className="question-bank-styling " style={{ color: 'black' }}>
                        {/*  <p className="heading text-dark">Question: {" " + selectedQuestionNumber} </p> */}
                        <div className="d-flex ps-2 ">
                          <div>  <span className={` ps-2 `}>{selectedQuestion.question ? parse(selectedQuestion.question) : null}</span> </div>
                        </div>

                        <div className="d-flex " style={{ display: 'flex' }}>
                          <strong className={`${correctAnswer == 'a' && 'bg-success text-white'}`}> (A)</strong>
                          <span className={` ps-2 ${correctAnswer == 'a' && 'bg-success text-white'}`}> {selectedQuestion.option1 ? parse(selectedQuestion.option1) : null} </span>
                        </div>
                        <div className="d-flex " style={{ display: 'flex' }}>
                          <strong className={`${correctAnswer == 'b' && 'bg-success text-white'} `}>(B)</strong>
                          <span className={` ps-2 ${correctAnswer == 'b' && 'bg-success text-white'}`}>{selectedQuestion.option2 ? parse(selectedQuestion.option2) : null}</span>
                        </div>
                        <div className="d-flex " style={{ display: 'flex' }}>
                          <strong className={`${correctAnswer == 'c' && 'bg-success text-white'} `}>(C)</strong>
                          <span className={` ps-2 ${correctAnswer == 'c' && 'bg-success text-white'}`}>{selectedQuestion.option3 ? parse(selectedQuestion.option3) : null}</span>
                        </div>
                        <div className="d-flex" style={{ display: 'flex' }}>
                          <strong className={`${correctAnswer == 'd' && 'bg-success text-white'} `}>(D)</strong>
                          <span className={` ps-2 ${correctAnswer == 'd' && 'bg-success text-white'}`}>{selectedQuestion.option4 ? parse(selectedQuestion.option4) : null}</span>
                        </div>

                        <div className="d-flex" style={{ display: 'flex' }}>
                          <strong >Explanation: </strong>
                          <span className={` ps-2 `}>{selectedQuestion.description ? parse(selectedQuestion.description) : null}</span>
                        </div>

                      </div>
                    </div>
                    {showQuestion && <div className="card-footer">
                      <div className="row d-flex justify-content-between">
                        <div className="col-md-3 d-flex justify-content-between">
                          <Stack direction="row" spacing={2}>
                            <ButtonMUI variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => { deleteItem(selectedQuestion.sn, 'Question', '/ugc/deleteQuestion/', setLoadQuestions, loadQuestions, 'post') }}>
                              Delete
                            </ButtonMUI>
                            <ButtonMUI variant="outlined" color="primary" endIcon={<FaEdit />} onClick={() => childRef.current.openModal(selectedQuestion)}>
                              Edit
                            </ButtonMUI>
                          </Stack>
                        </div>
                        <div className="col-md-3 d-flex justify-content-between">
                          <Stack direction="row" spacing={2}>
                            <ButtonMUI variant="outlined" color="secondary" startIcon={<SlArrowLeft size={10} />} onClick={() => setSelectedQuestionNumber(selectedQuestionNumber - 1)}
                              disabled={selectedQuestionNumber == 1}>
                              Previous
                            </ButtonMUI>
                            <ButtonMUI variant="outlined" color="success" endIcon={<SlArrowRight size={10} />} onClick={() => setSelectedQuestionNumber(selectedQuestionNumber + 1)} disabled={selectedQuestionNumber == quizQuestions?.length}>
                              Next
                            </ButtonMUI>
                          </Stack>
                        </div>
                      </div>
                    </div>
                    }
                  </div>

                </div>
              </Tab.Container>
              }
            </div>
          </div>

        </div>
      </div>

      <UGC_QuestionBankModal ref={childRef} setLoadQuestions={setLoadQuestions} loadQuestions={loadQuestions} setQuestionOffset={setQuestionOffset} />
    </Fragment >

  );
};

export default UGC_QuestionBank;
